import type {OrderFilter, OrderFiltersValue, OrderUrlFilters} from './types';

import {createOrderFilters} from './constants';

export const getToday = () => new Date().toISOString().split('T')[0];

export const createOrderFilterState = (
  urlFilters: OrderUrlFilters
): {
  filters: OrderFilter[];
  values: OrderFiltersValue;
} => {
  const filters = createOrderFilters();
  const startDate = urlFilters.start_date;
  const endDate = urlFilters.end_date;
  const hasDateFilter = Boolean(startDate && endDate);
  const selectedValues = {
    coupon: urlFilters.coupon ?? [],
    expedition_type: urlFilters.expedition_type ?? [],
    order_status: urlFilters.order_status ?? []
  };

  const hydratedFilters = filters.map((filter) => {
    if (filter.name === 'is_modified') {
      return {
        ...filter,
        selected: urlFilters.is_modified === true
      };
    }

    if (filter.name === 'date') {
      const matchingDateItem = filter.items?.find(
        (item) => item.date?.start_date === startDate && item.date?.end_date === endDate
      );

      return {
        ...filter,
        items: filter.items?.map((item) => ({
          ...item,
          selected:
            hasDateFilter &&
            (item.key === matchingDateItem?.key ||
              (!matchingDateItem && item.key === 'customDate')),
          date:
            item.key === 'customDate' && hasDateFilter && !matchingDateItem
              ? {start_date: startDate ?? null, end_date: endDate ?? null}
              : item.date
        }))
      };
    }

    if (filter.name in selectedValues) {
      const values = selectedValues[filter.name as keyof typeof selectedValues];
      return {
        ...filter,
        items: filter.items?.map((item) => ({
          ...item,
          selected: values.includes(item.value)
        }))
      };
    }

    return filter;
  });

  return {
    filters: hydratedFilters,
    values: createFilterValues(hydratedFilters)
  };
};

export const createOrderUrlFilters = (filters: OrderFilter[]): OrderUrlFilters => {
  const values: OrderUrlFilters = {};

  for (const filter of filters) {
    if (filter.name === 'date') {
      const selectedDate = filter.items?.find((item) => item.selected)?.date;
      if (selectedDate?.start_date && selectedDate.end_date) {
        values.start_date = selectedDate.start_date;
        values.end_date = selectedDate.end_date;
      }
    } else if (filter.name === 'is_modified' && filter.selected) {
      values.is_modified = true;
    } else if (['coupon', 'expedition_type', 'order_status'].includes(filter.name)) {
      const selectedItems = filter.items?.filter((item) => item.selected).map((item) => item.value);
      if (selectedItems?.length) {
        values[filter.name as 'coupon' | 'expedition_type' | 'order_status'] = selectedItems;
      }
    }
  }

  return values;
};

export const validateDateRange = (startDate?: string | null, endDate?: string | null) => {
  if (!startDate || !endDate) {
    return 'لطفا تاریخ شروع و پایان را وارد کنید';
  }

  const startDateTime = new Date(startDate);
  const endDateTime = new Date(endDate);
  const today = new Date();

  if (startDateTime > endDateTime) {
    return 'تاریخ شروع باید قبل از تاریخ پایان باشد';
  }
  if (startDateTime > today || endDateTime > today) {
    return 'بازه انتخابی نباید از تاریخ امروز بیشتر باشد';
  }
  if ((endDateTime.getTime() - startDateTime.getTime()) / 86_400_000 > 90) {
    return 'بازه زمانی نباید بیشتر از 90 روز باشد';
  }
  return '';
};

export const createFilterValues = (
  filters: OrderFilter[],
  fallback: OrderFiltersValue = {}
): OrderFiltersValue => {
  const values: OrderFiltersValue = {...fallback};

  for (const filter of filters) {
    if (filter.name === 'is_modified' && filter.selected) {
      values.is_modified = true;
    }

    if (filter.name === 'date') {
      const selectedDate = filter.items?.find((item) => item.selected)?.date;
      if (selectedDate?.start_date && selectedDate.end_date) {
        values.start_date = selectedDate.start_date;
        values.end_date = selectedDate.end_date;
      }
    }

    if (['coupon', 'expedition_type', 'order_status'].includes(filter.name)) {
      const selectedValues =
        filter.items?.filter((item) => item.selected).map((item) => item.value) ?? [];
      if (selectedValues.length > 0) {
        values[filter.name as 'coupon' | 'expedition_type' | 'order_status'] = selectedValues;
      }
    }
  }

  return values;
};
